import { GoDecision, PortalStatus, PositionStage } from '@clawhire/shared';
import type { QueryParams } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import type { FilterDef, FilterValues } from '@/components/data/filters';
import type { DirectoryUser, PartnerRef } from '../types';

/** Filters whose query param takes a single value (the FilterBar enum control is a multi-select). */
const SINGLE_VALUE_KEYS = new Set(['portalStatus', 'goDecision', 'intermediary', 'ownerId']);

/** Rows fetched per list request; the API caps `pageSize` at 200 and volumes are small. */
export const LIST_PAGE_SIZE = 200;

/** Filter definitions; keys map 1:1 to `GET /positions` query params. */
export function buildFilterDefs(
  intermediaries: readonly PartnerRef[],
  users: readonly DirectoryUser[] = [],
): FilterDef[] {
  return [
    { key: 'text', label: 'Search', type: 'text', placeholder: 'Role, request no, JD ref…' },
    { key: 'stage', label: 'Stage', type: 'enum', options: enumOptions(PositionStage) },
    { key: 'portalStatus', label: 'Portal', type: 'enum', options: enumOptions(PortalStatus) },
    { key: 'goDecision', label: 'Go decision', type: 'enum', options: enumOptions(GoDecision) },
    {
      key: 'intermediary',
      label: 'Intermediary',
      type: 'enum',
      options: intermediaries.map((p) => ({ value: p.id, label: p.name })),
    },
    {
      key: 'ownerId',
      label: 'Owner',
      type: 'enum',
      options: users.map((u) => ({ value: u.id, label: u.name })),
    },
    { key: 'openOnly', label: 'Open only', type: 'boolean' },
    { key: 'urgentOnly', label: 'Urgent only', type: 'boolean' },
  ];
}

/** View filters → query params (single-valued params take the first selected value). */
export function toListQuery(filters: FilterValues): QueryParams {
  const query: QueryParams = { pageSize: LIST_PAGE_SIZE };
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      query[key] = SINGLE_VALUE_KEYS.has(key) ? value[0] : value;
    } else {
      query[key] = value;
    }
  }
  return query;
}
